<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref } from 'vue';
import { Printer } from 'lucide-vue-next';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import Spinner from '@/components/ui/Spinner.vue';
import { drugUnitValue, formatQuantity } from '@/composables/inventory/inventoryTable';
import { useInventoryScope } from '@/composables/inventory/useInventoryScope';
import { useMedicineCountSheet } from '@/plugins/store/actions/queries/inventory';

const isOpen = defineModel<boolean>('open', { required: true });

const { scope } = useInventoryScope();
const locationIds = computed(() => scope.value.locationIds);
const { data, isFetching, isError, refetch } = useMedicineCountSheet(locationIds, { enabled: computed(() => isOpen.value && locationIds.value.length > 0) });

const farms = computed(() => data.value ?? []);
const hasRows = computed(() => farms.value.some((farm) => farm.rows.length > 0));

/** Off prints a blind sheet: the counter writes down what is on the shelf without seeing what the ledger expects. */
const showCalculated = ref(true);
const today = new Date();

/** Marks the page as being printed, so the print stylesheet below can reduce it to this dialog's sheet. */
const PRINTING_CLASS = 'count-sheet-printing';

function stopPrinting() {
    document.body.classList.remove(PRINTING_CLASS);
}

async function print() {
    document.body.classList.add(PRINTING_CLASS);
    window.addEventListener('afterprint', stopPrinting, { once: true });

    await nextTick();
    window.print();
}

onBeforeUnmount(() => {
    window.removeEventListener('afterprint', stopPrinting);
    stopPrinting();
});
</script>

<template>
    <Dialog v-model="isOpen" class="count-sheet-dialog sm:max-w-[900px]">
        <div class="space-y-6 print:hidden">
            <DialogHeader>
                <DialogTitle>{{ $t('views.health.inventory.countSheet.title') }}</DialogTitle>
                <DialogDescription class="mt-2 text-gray-500">{{ $t('views.health.inventory.countSheet.description') }}</DialogDescription>
            </DialogHeader>
            <div class="flex flex-wrap items-center justify-between gap-4">
                <label class="flex items-center gap-2 cursor-pointer">
                    <Checkbox v-model:checked="showCalculated" />
                    <span>{{ $t('views.health.inventory.countSheet.showCalculated') }}</span>
                </label>
                <Button :disabled="!hasRows || isFetching" @click="print">
                    <Printer class="size-4" aria-hidden="true" />
                    {{ $t('views.health.inventory.buttons.print') }}
                </Button>
            </div>
        </div>

        <div v-if="isError" class="py-10 text-center text-gray-500 space-y-4 print:hidden">
            <div>{{ $t('views.pigs.records.errors.couldNotLoadData') }}</div>
            <Button variant="outline" @click="refetch()">{{ $t('views.pigs.records.errors.retry') }}</Button>
        </div>
        <div v-else-if="isFetching && farms.length === 0" class="py-10 flex justify-center print:hidden">
            <Spinner class="size-6 animate-spin" />
        </div>
        <div v-else-if="!hasRows" class="py-10 text-center text-gray-500 print:hidden">{{ $t('views.health.inventory.countSheet.empty') }}</div>
        <div v-else class="count-sheet max-h-[60vh] overflow-auto rounded-lg border border-gray-200 print:max-h-none print:overflow-visible print:border-0 print:rounded-none">
            <template v-for="farm in farms" :key="farm.farm_id">
                <section v-if="farm.rows.length" class="count-sheet-page p-6 space-y-4 border-b border-gray-200 last:border-b-0">
                    <header class="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1">
                        <div>
                            <h2 class="text-xl font-semibold">{{ $t('views.health.inventory.countSheet.sheetHeading') }}</h2>
                            <div class="text-lg">
                                {{ farm.farm_name }}
                                <span v-if="farm.farm_number" class="text-gray-500">({{ $t('views.health.inventory.countSheet.farmNumber') }} {{ farm.farm_number }})</span>
                            </div>
                        </div>
                        <div class="text-gray-600">{{ $t('views.health.inventory.countSheet.printed') }} {{ $d(today, 'short') }}</div>
                    </header>
                    <table class="w-full border-collapse text-normal">
                        <thead>
                            <tr class="text-left">
                                <th class="border border-gray-400 px-2 py-1.5 font-semibold">{{ $t('views.health.inventory.table.headings.drug') }}</th>
                                <th class="border border-gray-400 px-2 py-1.5 font-semibold">{{ $t('views.health.inventory.countSheet.unit') }}</th>
                                <th v-if="showCalculated" class="border border-gray-400 px-2 py-1.5 text-right font-semibold">{{ $t('views.health.inventory.countSheet.calculated') }}</th>
                                <th class="border border-gray-400 px-2 py-1.5 font-semibold w-32">{{ $t('views.health.inventory.countSheet.counted') }}</th>
                                <th class="border border-gray-400 px-2 py-1.5 font-semibold w-56">{{ $t('views.health.inventory.countSheet.remark') }}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="row in farm.rows" :key="drugUnitValue(row)" class="h-10 break-inside-avoid">
                                <td class="border border-gray-400 px-2 py-1.5">{{ row.drug_name }}</td>
                                <td class="border border-gray-400 px-2 py-1.5">{{ row.unit_name }}</td>
                                <td v-if="showCalculated" class="border border-gray-400 px-2 py-1.5 text-right">{{ formatQuantity(row.calculated) }}</td>
                                <td class="border border-gray-400 px-2 py-1.5"></td>
                                <td class="border border-gray-400 px-2 py-1.5"></td>
                            </tr>
                        </tbody>
                    </table>
                    <div class="flex gap-x-12 pt-10">
                        <div class="flex-1 border-t border-gray-500 pt-1 text-gray-600">{{ $t('views.health.inventory.countSheet.countedBy') }}</div>
                        <div class="w-48 border-t border-gray-500 pt-1 text-gray-600">{{ $t('views.health.inventory.countSheet.date') }}</div>
                    </div>
                </section>
            </template>
        </div>
    </Dialog>
</template>

<!-- Unscoped: the dialog is portalled out of this component's subtree, and the page around it has to be hidden. -->
<style>
@media print {
    /* Everything outside the dialog's portal (the app, toasts) is left off the paper. */
    body.count-sheet-printing > *:not(:has(.count-sheet)) {
        display: none !important;
    }

    body.count-sheet-printing {
        overflow: visible !important;
        height: auto !important;
    }

    /* The portal's overlay is a fixed, scrolling backdrop on screen; on paper it is a plain block that flows over as many pages as the sheets need. */
    body.count-sheet-printing > *:has(.count-sheet) {
        position: static !important;
        display: block !important;
        overflow: visible !important;
        background: none !important;
        backdrop-filter: none !important;
        padding: 0 !important;
    }

    body.count-sheet-printing .count-sheet-dialog {
        max-width: none !important;
        width: 100% !important;
        padding: 0 !important;
        border: 0 !important;
        box-shadow: none !important;
        animation: none !important;
    }

    /* The dialog's own close button. */
    body.count-sheet-printing .count-sheet-dialog > button {
        display: none !important;
    }

    /* One page per farm. The page margin is zero app-wide, so the sheet carries its own. */
    body.count-sheet-printing .count-sheet-page {
        break-after: page;
        padding: 15mm !important;
        border: 0 !important;
    }

    body.count-sheet-printing .count-sheet-page:last-child {
        break-after: auto;
    }
}
</style>
